import { useContext } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { RecipientPopover } from '@bonusly/common-recipient-popover';
import { Link, LinkProps, Popover, PopoverBody, PopoverContent, PopoverTrigger, Portal, Text } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

import { useHashtagLink } from '../../../../../modules/bonus-feed/features/recognition/hooks/useHashtagLink';
import { AppPath } from '../../../../../modules/top-navbar/config/router';
import { pathWithId } from '../../../../../modules/top-navbar/utils/api';
import { Bonus } from '../../../../../types/bonus';
import { getBonusType } from '../../../../../utils/bonus';
import { populateEmojis } from '../../../../../utils/emoji';
import { BonusTextContext } from '../../components/bonus/bonus-text';
import { Chip } from '../../components/common/chip';
import { HashtagDetail } from '../../components/common/hashtag/hashtag-detail';
import { normalizeLink } from '../../components/common/link/utils';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { useHashtags } from '../../hooks/use-hashtags';

export const Amount = (text: string) => {
	const bonus = useContext(BonusTextContext);
	const bonusType = bonus ? getBonusType(bonus) : '';

	let styles = {};

	if (bonus && bonusType === 'celebration') {
		styles = {
			backgroundColor: 'none',
			border: '1px solid',
			borderColor: 'points.fg',
			color: 'points.fg',
		};
	} else {
		styles = {
			backgroundColor: 'points.bgAlpha',
			color: 'points.fg',
		};
	}

	return (
		<Chip data-testid="amount-chip" as="span" size="xs" {...styles}>
			{text}
		</Chip>
	);
};

const StyleLink = (props: LinkProps) => {
	return <Link color="text.interactive" {...props} />;
};

export const NewLine = () => <br />;

const MARKDOWN_LINK_REGEX = /(!?\[([^\]]*)\]\(([^)]*)\))/g;
const INLINE_FORMAT_REGEX = /(\*\*\*(?!\s)(.+?)(?<!\s)\*\*\*|\*\*(?!\s)(.+?)(?<!\s)\*\*|\*(?!\s)(.+?)(?<!\s)\*)/g;

function formatInlineText(text: string, keyPrefix: number): React.ReactNode[] {
	const parts = text.split(INLINE_FORMAT_REGEX);
	if (parts.length === 1) return [text];

	const nodes: React.ReactNode[] = [];
	let i = 0;
	while (i < parts.length) {
		const plain = parts[i];
		if (plain) nodes.push(plain);
		i++;

		if (i < parts.length) {
			const boldItalicContent = parts[i + 1];
			const boldContent = parts[i + 2];
			const italicContent = parts[i + 3];
			if (boldItalicContent) {
				nodes.push(
					<strong key={`${keyPrefix}-${i}`}>
						<em>{boldItalicContent}</em>
					</strong>
				);
			} else if (boldContent) {
				nodes.push(<strong key={`${keyPrefix}-${i}`}>{boldContent}</strong>);
			} else if (italicContent) {
				nodes.push(<em key={`${keyPrefix}-${i}`}>{italicContent}</em>);
			}
			i += 4;
		}
	}
	return nodes;
}

export const TextString = (text: string) => {
	const emojiText = populateEmojis(text);
	const linkParts = emojiText.split(MARKDOWN_LINK_REGEX);

	if (linkParts.length === 1) return <>{formatInlineText(emojiText, 0)}</>;

	const elements: React.ReactNode[] = [];
	let i = 0;
	while (i < linkParts.length) {
		const plain = linkParts[i];
		if (plain) elements.push(...formatInlineText(plain, i));
		i++;

		if (i < linkParts.length) {
			const fullMatch = linkParts[i];
			const linkText = linkParts[i + 1];
			const url = linkParts[i + 2];
			if (fullMatch?.startsWith('!')) {
				elements.push(<img key={i} src={normalizeLink(url)} alt={linkText} style={{ display: 'inline', maxHeight: '1.5em' }} />);
			} else {
				elements.push(
					<StyleLink key={i} href={normalizeLink(url)} target="_blank" rel="noopener noreferrer">
						{linkText}
					</StyleLink>
				);
			}
			i += 3;
		}
	}

	return <>{elements}</>;
};

const createUserSegment =
	<T extends keyof Pick<Bonus, 'receivers' | 'tagged_users'>>(attr: T, color: string, event: BonusFeedTrackedAction) =>
	// eslint-disable-next-line react/display-name
	(text: string) => {
		const bonus = useContext(BonusTextContext);

		const trackClick = useTrackBonusFeedItemEvent(bonus as Bonus, event);
		const textUsername = text.substring(1);

		// User is used just for cc users
		const user = bonus?.[attr]?.find(({ all_usernames, username }) => {
			// Check all_usernames if available, otherwise fall back to username
			return all_usernames?.some((u) => u === textUsername) || username === textUsername;
		});

		const userToken = bonus?.reason_tokens?.find(({ type, token }) => {
			const userTypes = ['username'];
			return userTypes.includes(type) && token === text;
		});

		const group = bonus?.reason_tokens?.find(({ type, token }) => {
			const groupTypes = ['everyone', 'department', 'location', 'team', 'csv_upload', 'custom_group'];
			return groupTypes.includes(type) && token === text;
		});

		if (group) {
			return (
				<RecipientPopover recipient={Recipient.fromReasonToken(group, bonus?.[attr] || [])} openDelay={500} placement="top">
					<Text as="span" role="button" tabIndex={0} color={color} cursor="pointer" fontWeight="medium" size="lg" _hover={{ textDecor: 'underline' }}>
						{text}
					</Text>
				</RecipientPopover>
			);
		}

		if (!userToken && !user) return TextString(text);

		const userId = userToken?.user_ids?.[0] || user?.id || '';

		return (
			<RecipientPopover
				recipient={
					new Recipient({
						id: userId,
						display_name: '',
						profile_pic_url: '',
						username: userToken?.token?.substring(1) || user?.username || '',
					})
				}
				openDelay={500}
				placement="top"
			>
				<Link
					as={RouterLink}
					to={pathWithId(AppPath.Profile, userId)}
					color={color}
					fontWeight="medium"
					onClick={(e) => {
						trackClick();
						e.stopPropagation();
					}}
				>
					{textUsername === (userToken?.token?.substring(1) || user?.username)
						? text
						: text.substring(0, 1) + (userToken?.token?.substring(1) || user?.username)}
				</Link>
			</RecipientPopover>
		);
	};

export const Mention = createUserSegment('receivers', 'text.interactive', BonusFeedTrackedAction.RecipientClick);

export const UserTag = createUserSegment('tagged_users', 'text.softest', BonusFeedTrackedAction.TaggedUserClick);

export const Hashtag = (text: string) => {
	const hashtagLinkUrl = useHashtagLink(text);
	const navigate = useNavigate();
	const { data } = useHashtags({
		params: {
			display_format: 'core_value_hashtags',
		},
	});

	const core_value = data?.data?.result?.values?.find(({ value }) => value === text);
	const hashtag = {
		core_value,
		hashtag: text,
	};

	const handleClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		navigate(hashtagLinkUrl);
		window.scrollTo(0, 0);
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			event.stopPropagation();
			navigate(hashtagLinkUrl);
			window.scrollTo(0, 0);
		}
	};

	return (
		<Popover isLazy openDelay={500} placement="top" trigger="hover">
			<PopoverTrigger>
				<Text
					as="span"
					role="button"
					tabIndex={0}
					cursor="pointer"
					color="hashtag.fg"
					fontSize={150}
					fontWeight="medium"
					wordBreak="break-word"
					_hover={{ color: 'hashtag.fgHover' }}
					onClick={handleClick}
					onKeyDown={handleKeyDown}
				>
					{text}
				</Text>
			</PopoverTrigger>
			<Portal>
				<PopoverContent borderColor="bg.subtle" boxShadow="xl">
					<PopoverBody p={5}>
						<HashtagDetail hashtag={hashtag} />
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	);
};
